import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Col, Container, Row } from "react-bootstrap";
import "../assets/styles/scheduledChanges.scss";
import ScheduledChangesIcon from "../assets/images/timetable_rgb_red.svg";
import ScheduledChangesTable from "../components/ScheduledChanges/ScheduledChangesTable";
import ScheduledChangesFilters from "../components/ScheduledChanges/ScheduledChangesFilters";
import ScheduledChangesButtons from "../components/ScheduledChanges/ScheduledChangesButtons";
import {
  approveScheduledChange,
  getScheduledChangesHistory,
  rejectScheduledChange,
} from "../services/scheduledChangesApi";
import PaginationComponent from "../components/PaginationComponent";

/* ------------------------------------------------------------------ */
/*  Pure helper – resolves a date-range preset to { from, to } dates  */
/* ------------------------------------------------------------------ */
function resolveDateRange(filters, rangeKey, startKey, endKey) {
  const range = filters[rangeKey];
  if (!range) return { from: null, to: null };

  const now = new Date();
  const startOfDay = (d) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const endOfDay = (d) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

  if (range === "custom") {
    const s = filters[startKey];
    const e = filters[endKey];
    return {
      from: s ? new Date(`${s}T00:00:00.000`) : null,
      to: e ? new Date(`${e}T23:59:59.999`) : null,
    };
  }
  if (range === "today") return { from: startOfDay(now), to: endOfDay(now) };
  if (range === "yesterday") {
    const d = new Date(now);
    d.setDate(d.getDate() - 1);
    return { from: startOfDay(d), to: endOfDay(d) };
  }
  if (range === "last7Days") {
    const d = new Date(now);
    d.setDate(d.getDate() - 6);
    return { from: startOfDay(d), to: endOfDay(now) };
  }
  if (range === "last30Days") {
    const d = new Date(now);
    d.setDate(d.getDate() - 29);
    return { from: startOfDay(d), to: endOfDay(now) };
  }
  if (range === "thisMonth") {
    return {
      from: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)),
      to: endOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    };
  }
  if (range === "previousMonth") {
    return {
      from: startOfDay(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
      to: endOfDay(new Date(now.getFullYear(), now.getMonth(), 0)),
    };
  }
  return { from: null, to: null };
}

/* ------------------------------------------------------------------ */
/*  Pure helper – builds the full query object for the API call        */
/* ------------------------------------------------------------------ */
const SORT_BY_MAP = {
  createdAt: "CreatedAt",
  scheduledTime: "ScheduledTime",
  rollbackScheduledTime: "RollbackScheduledTime",
  status: "Status",
  terminalDr: "TerminalDr",
  terminalEx: "TerminalEx",
  requestedBy: "RequestedBy",
  approvedBy: "ApprovedBy",
  changes: "ChangeDetailsJson",
};

function buildQuery(filters, page, pageSize, sort) {
  const scheduled = resolveDateRange(filters, "dateRange", "planStartDate", "planEndDate");
  const rollback = resolveDateRange(
    filters,
    "withdrawalDateRange",
    "withdrawalStartDate",
    "withdrawalEndDate"
  );

  const query = {};

  if (scheduled.from) query.ScheduledTime_From = scheduled.from.toISOString();
  if (scheduled.to) query.ScheduledTime_To = scheduled.to.toISOString();
  if (rollback.from) query.RollbackScheduledTime_From = rollback.from.toISOString();
  if (rollback.to) query.RollbackScheduledTime_To = rollback.to.toISOString();
  if (filters.terminalDr) query.TerminalDr = filters.terminalDr;
  if (filters.terminalEx) query.TerminalEx = filters.terminalEx;
  if (filters.soN) query.Status = filters.soN;

  query.PageNumber = page;
  query.PageSize = pageSize;

  if (sort?.key) {
    query.SortBy = SORT_BY_MAP[sort.key] || sort.key;
    query.SortDirection = sort.direction === "desc" ? "Desc" : "Asc";
  }

  return query;
}

/* ================================================================== */
/*  Component                                                          */
/* ================================================================== */
const ScheduledChanges = () => {
  const [draftFilters, setDraftFilters] = useState({});

  const appliedFiltersRef = useRef({});

  const [data, setData] = useState({
    items: [],
    itemsCount: null,
    totalRecords: 0,
    totalPages: 0,
    pageNumber: 1,
    pageSize: 20,
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const [rememberedTotalCount, setRememberedTotalCount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  const [sortConfig, setSortConfig] = useState({ key: "scheduledTime", direction: "desc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const [fetchVersion, setFetchVersion] = useState(0);
  const currentPageRef = useRef(currentPage);
  const itemsPerPageRef = useRef(itemsPerPage);
  const sortConfigRef = useRef(sortConfig);
  const lastFetchedVersionRef = useRef(0);
  const abortControllerRef = useRef(null);

  currentPageRef.current = currentPage;
  itemsPerPageRef.current = itemsPerPage;
  sortConfigRef.current = sortConfig;

  const handleFilterChange = useCallback((field, value) => {
    setDraftFilters((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSearch = useCallback(() => {
    appliedFiltersRef.current = { ...draftFilters };
    setCurrentPage(1);
    setRememberedTotalCount(null);
    setSuccessMessage("");
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  }, [draftFilters]);

  const handleSortChange = useCallback((next) => {
    setSortConfig(next);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  }, []);

  const handlePageChange = useCallback((page) => {
    if (Number(page) === Number(currentPageRef.current)) return;
    setCurrentPage(page);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  }, []);

  const handleItemsPerPageChange = useCallback((size) => {
    setItemsPerPage(size);
    setCurrentPage(1);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  }, []);

  const getRecordGuid = useCallback((row) => {
    return (
      row?.changeId ??
      row?.ChangeId ??
      row?.id ??
      row?.Id ??
      row?.scheduledChangeId ??
      row?.historyId ??
      null
    );
  }, []);

  const isGuid = (value) => {
    if (typeof value !== "string") return false;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      value
    );
  };

  const getSelectedIds = useCallback(() => {
    return selectedRows
      .map((row) => getRecordGuid(row))
      .filter((id) => isGuid(id));
  }, [getRecordGuid, selectedRows]);

  const handleApprove = useCallback(async () => {
    if (isApproving) return;

    const ids = getSelectedIds();
    if (ids.length === 0) {
      setError(new Error("No valid record selected for approval."));
      setSuccessMessage("");
      return;
    }

    setIsApproving(true);
    setError(null);
    setSuccessMessage("");

    try {
      await Promise.all(ids.map((id) => approveScheduledChange(id)));
      setSuccessMessage("Zmiana została zaakceptowana.");
      setSelectedRows([]);
      setFetchVersion((v) => v + 1);
    } catch (e) {
      setError(e);
      setSuccessMessage("");
    } finally {
      setIsApproving(false);
    }
  }, [getSelectedIds, isApproving]);

  const handleReject = useCallback(async () => {
    if (isRejecting) return;

    const ids = getSelectedIds();
    if (ids.length === 0) {
      setError(new Error("No valid record selected for rejection."));
      setSuccessMessage("");
      return;
    }

    setIsRejecting(true);
    setError(null);
    setSuccessMessage("");

    try {
      await Promise.all(ids.map((id) => rejectScheduledChange(id)));
      setSuccessMessage("Zmiana została odrzucona.");
      setSelectedRows([]);
      setFetchVersion((v) => v + 1);
    } catch (e) {
      setError(e);
      setSuccessMessage("");
    } finally {
      setIsRejecting(false);
    }
  }, [getSelectedIds, isRejecting]);

  useEffect(() => {
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  }, []);

  /* ---- data fetching ---- */
  useEffect(() => {
    if (lastFetchedVersionRef.current === fetchVersion) return;
    lastFetchedVersionRef.current = fetchVersion;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const pageNumber = currentPageRef.current;
    const pageSize = itemsPerPageRef.current;
    const sort = sortConfigRef.current;

    const query = buildQuery(
      appliedFiltersRef.current,
      pageNumber,
      pageSize,
      sort
    );


    let cancelled = false;

    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await getScheduledChangesHistory(query, controller.signal);
        if (cancelled) return;

        const payload = res?.data;
        const nextItems = Array.isArray(payload)
          ? payload
          : payload?.items ?? payload?.data ?? [];
        const responsePageNumber = Number(
          payload?.pageNumber ?? payload?.PageNumber ?? pageNumber
        );
        const responseTotalCountRaw =
          payload?.totalCount ??
          payload?.TotalCount ??
          payload?.itemsCount ??
          payload?.totalRecords ??
          payload?.TotalRecords ??
          payload?.total ??
          payload?.count;
        const responseTotalCount =
          Number.isFinite(Number(responseTotalCountRaw)) &&
          Number(responseTotalCountRaw) >= 0
            ? Number(responseTotalCountRaw)
            : null;
        const responseTotalPagesRaw =
          payload?.totalPages ?? payload?.TotalPages;
        const responseTotalPages =
          Number.isFinite(Number(responseTotalPagesRaw)) &&
          Number(responseTotalPagesRaw) > 0
            ? Number(responseTotalPagesRaw)
            : 0;

        if (responsePageNumber === 1) {
          setRememberedTotalCount(responseTotalCount);
        }

        setData({
          items: nextItems,
          itemsCount: responseTotalCount,
          totalRecords:
            Number.isFinite(Number(payload?.totalRecords))
              ? Number(payload.totalRecords)
              : 0,
          totalPages: responseTotalPages,
          pageNumber: responsePageNumber,
          pageSize:
            Number.isFinite(Number(payload?.pageSize)) && Number(payload.pageSize) > 0
              ? Number(payload.pageSize)
              : pageSize,
        });
      } catch (e) {
        if (!cancelled && e?.name !== "CanceledError" && e?.code !== "ERR_CANCELED") {
          setError(e);
          setData({
            items: [],
            itemsCount: null,
            totalRecords: 0,
            totalPages: 0,
            pageNumber: 1,
            pageSize,
          });
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [fetchVersion]);

  /* ---- render ---- */
  return (
    <div className="scheduledChanges-container">
      <Container fluid>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <img
              src={ScheduledChangesIcon}
              alt="Scheduled Changes Icon"
              style={{ width: "30px", height: "30px" }}
            />
          </Col>
          <Col>
            <h3 style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
              Zaplanowane zmiany
            </h3>
          </Col>
        </Row>

        <Row className="align-items-center">
          <Col md={9}>
            <ScheduledChangesFilters
              filters={draftFilters}
              onFilterChange={handleFilterChange}
            />
          </Col>
          <Col md={3} className="d-flex justify-content-end">
            <ScheduledChangesButtons
              onSearch={handleSearch}
              onApprove={handleApprove}
              isApproving={isApproving}
              onReject={handleReject}
              isRejecting={isRejecting}
            />
          </Col>
        </Row>

        <Row>
          <Col>
            {successMessage && (
              <Alert variant="success" className="mt-2">
                {successMessage}
              </Alert>
            )}
            <ScheduledChangesTable
              data={data.items}
              onSortChange={handleSortChange}
              onSelectionChange={setSelectedRows}
              isLoading={isLoading}
              errorMessage={error ? "Nie udało się pobrać historii zmian." : ""}
            />
          </Col>
        </Row>

        <Row className="align-items-center mt-4">
          <PaginationComponent
            itemsPerPage={data.pageSize}
            dataCount={data.items.length}
            itemsCount={data.itemsCount ?? rememberedTotalCount}
            totalCount={rememberedTotalCount}
            pageNumber={data.pageNumber}
            totalPages={data.totalPages}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            itemsPerPageOptions={[ 20, 100, 500]}
          />
        </Row>
      </Container>
    </div>
  );
};

export default ScheduledChanges;
